
import { HttpClient } from '@angular/common/http';
import {
    Component,
    effect,
    inject,
    input,
    model,
    OnInit,
    output,
} from '@angular/core';
import { AuthenticationService } from '@my-aon/auth';
import { environment } from '@environment';
import { TranslateService } from '@ngx-translate/core';
import { DxFormModule, DxScrollViewModule } from 'devextreme-angular';
import { Item as DxFormItem } from 'devextreme/ui/form';
import {
    InterceptedError,
    NotifyService,
} from '../../lib';
import { mapFormAddTranslatedLabels, validateGroup } from '@my-aon/utils';
import { LoadIndicatorComponent } from '../load-indicator/load-indicator.component';

interface IContactUsModel {
    FirstName: string;
    LastName: string;
    Email: string;
    ContactMotive: string;
    Body: string;
}

@Component({
    selector: 'app-form-contact-us',
    imports: [
    DxFormModule,
    DxScrollViewModule,
    LoadIndicatorComponent
],
    templateUrl: './form-contact-us.component.html',
    styleUrl: './form-contact-us.component.scss',
})
export class FormContactUsComponent implements OnInit {
    private readonly translateService = inject(TranslateService);
    private readonly auth = inject(AuthenticationService);
    private readonly http = inject(HttpClient);
    private readonly notify = inject(NotifyService);

    formData: Partial<IContactUsModel> = {};

    validationGroupName = input<string>(
        'FormContactUsComponent' + 'ValidationGroupName'
    );
    validate = async () => {
        return await validateGroup(this.validationGroupName());
    };

    submited = model<boolean>();
    loadIndicatorVisible = false;

    constructor() {
        effect(() => {
            this.submited() && setTimeout(() => this.formSubmit(), 12);
        });
    }

    async ngOnInit() {
        const user = this.auth.$user()!;

        const userNameParts = user.Name.split(' ');
        this.formData = {
            FirstName: userNameParts[0],
            LastName: userNameParts[userNameParts.length - 1],
            Email: user.Email,
        };
        await this.generateFormItems();
    }

    items: DxFormItem[] = [];
    async generateFormItems() {
        this.items = (
            [
                {
                    itemType: 'group',
                    colCount: 2,
                    items: [
                        {
                            dataField: 'FirstName',
                            isRequired: true,
                            editorOptions: {
                                readOnly: true,
                                maxLength: 50,
                            },
                        },
                        {
                            dataField: 'LastName',
                            isRequired: true,
                            editorOptions: {
                                readOnly: true,
                                maxLength: 50,
                            },
                        },
                    ],
                },
                {
                    dataField: 'Email',
                    isRequired: true,
                    validationRules: [
                        { type: 'required' },
                        { type: 'email', ignoreEmptyValue: true },
                    ],
                    editorOptions: {
                        readOnly: true,
                        maxLength: 100,
                    },
                },
                {
                    dataField: 'ContactMotive',
                    isRequired: true,
                    editorOptions: {
                        maxLength: 100,
                    },
                },
                {
                    dataField: 'Body',
                    isRequired: true,
                    editorType: 'dxTextArea',
                    editorOptions: {
                        maxLength: 1000,
                        height: 200,
                    },
                },
            ] as DxFormItem[]
        ).map(
            mapFormAddTranslatedLabels(
                this.translateService,
                'CONTACT_US.FIELD'
            )
        );
    }

    formSubmitTrigger: boolean = false;
    formSubmit = async (e?: any) => {
        if (this.loadIndicatorVisible)
            //already submited
            return;

        if (!(await this.validate())) {
            this.submited.set(false);
            return;
        }
        this.submit();
    };

    private submit = async () => {
        this.loadIndicatorVisible = true;

        this.http
            .post(`${environment.apiUrl}ContactUs`, this.formData)
            .subscribe({
                next: () => {
                    this.notify.success(
                        this.translateService.instant(
                            'CONTACT_US.NOTIFY_CONTACT_SENT_BODY'
                        ),
                        this.translateService.instant(
                            'CONTACT_US.NOTIFY_CONTACT_SENT_TITLE'
                        ), 10000
                    );
                    this.close();
                },
                error: (err: InterceptedError) => {
                    console.error(err);
                    this.notify.error(
                        'Please contact support.',
                        'There was an error while processing your request'
                    );
                    this.loadIndicatorVisible = false;
                    this.submited.set(false);
                },
            });
    };

    onClose = output();
    close = () => {
        this.onClose.emit();
    };
}
